/**
 * Die Rollen eines Messkonzepts — wie `ui_kits/dashboard/MesskonzeptScreen.jsx`
 * sie zeigt: in der Skizze als Kasten oder Strang, in der Liste als Zeile.
 *
 * **Eine Rolle trägt einen oder mehrere Steckplätze.** Der Server liefert die
 * Vorlage einer Fassung expandiert — je Wohneinheit, je Erzeuger und je
 * Verbraucher ein Steckplatz (HM-080). Der Entwurf denkt in Rollen:
 * „Wohnungszähler" ist eine Zeile mit allen Wohnungszählern darin, und in der
 * Skizze ein Strang mit einem Kasten je Zähler und einem Plus darunter. Diese
 * Rechnung fasst die Steckplätze zu diesen Rollen zusammen — **an einer
 * Stelle**, damit Skizze und Liste nie verschieden gruppieren.
 *
 * **Ein Kasten je Zähler, nie „+1" in einem Kasten.** So verlangt es der
 * Entwurf, und er begründet es: Sonst steht im Bild eine Zahl, wo der Nutzer
 * einen Namen sucht. Die Zusammenfassung „6 · 4 zugeordnet" aus HM-080 ist
 * deshalb entfallen; die Skizze wächst stattdessen nach unten.
 *
 * **Drei Arten Aufschlüsselung sind keine Rollen**, sondern stehen im Kasten
 * „Verbraucher" hinter einem Zähler:
 * die Verbraucher eines Hauses hinter „Verbrauch im Haus" (HM-110,
 * `consumersBehindHouse`), die Verbraucher einer Wohneinheit hinter deren
 * Zähler (HM-111) — und das gilt auch für die selbst genutzte Einheit: Unter
 * ihr hängen **parallel** der Erzeugungszähler (eine Rolle mit eigenem Zähler)
 * und der Kasten „Verbraucher · Einheit" mit ihren Verbrauchern.
 *
 * Reine Rechnung ohne React.
 */
import type { ApiComponents } from "@hausmeter/sdk";
import { consumersBehindHouse, hasUnmeteredRest } from "./concept-sketch-rest.js";

type Template = ApiComponents["schemas"]["ConceptTemplate"];
type Slot = ApiComponents["schemas"]["ConceptTemplateSlot"];

export type RoleFamily = "single" | "own_unit" | "units" | "generators" | "consumers";

/** Wie sich eine Rolle um einen Steckplatz erweitern lässt — `null`, wenn gar nicht. */
export type RoleAddition = "units" | "consumers" | "generators" | null;

export interface ConceptRole {
  readonly key: string;
  readonly label: string;
  readonly hint: string;
  readonly delivers: readonly string[];
  readonly family: RoleFamily;
  readonly slots: readonly Slot[];
  /** Ob unter den Steckplätzen einer Pflicht ist. */
  readonly required: boolean;
  /** Mehrere Steckplätze möglich: in der Skizze ein Strang statt eines Kastens. */
  readonly many: boolean;
  readonly add: RoleAddition;
  /** Erzeugung fließt in die Verteilung hinein, ein Verbrauch aus ihr heraus. */
  readonly up: boolean;
}

export interface ConceptStructure {
  readonly connection: ConceptRole | null;
  /** Ein eigener Zählpunkt am Haus vorbei (Volleinspeisung). */
  readonly separate: ConceptRole | null;
  /** Die Abzweige an der Hausverteilung, in der Reihenfolge der Skizze. */
  readonly branches: readonly ConceptRole[];
  /** Die Erzeugungszähler unter der selbst genutzten Einheit. */
  readonly ownChildren: readonly ConceptRole[];
  /**
   * Verbraucher hinter dem Zähler einer Wohneinheit, je Steckplatz der Einheit —
   * auch der selbst genutzten.
   */
  readonly behindUnit: ReadonlyMap<string, readonly Slot[]>;
  /** Verbraucher hinter „Verbrauch im Haus". */
  readonly houseConsumers: readonly Slot[];
  /** Ob es „Verbrauch im Haus" gibt — einen Rest, den kein Abzweig zählt. */
  readonly houseRest: boolean;
  /** Alle Rollen für die Liste, in ihrer Reihenfolge. */
  readonly roles: readonly ConceptRole[];
}

/**
 * Ob ein Steckplatz **Erzeugung** liefert (HM-122, HM-124) — dahinter stehen die
 * Erzeugungsanlagen. An einer Stelle, weil Skizze und Editor dieselbe Frage
 * stellen: Der Kasten im Bild und der Weg in der Liste dürfen nicht
 * auseinanderlaufen. Gefragt wird, was der Steckplatz **liefert**, nicht wie
 * sein Schlüssel heißt — der ist opak (ADR 0020).
 */
export function isGenerationSlot(slot: Slot): boolean {
  return slot.placement !== "connection" && slot.delivers.includes("Erzeugung");
}

/**
 * Welche Arten von Erzeugungsanlagen hinter einem Steckplatz stehen können
 * (HM-129) — leer, wenn keine. Hinter einem Erzeugungszähler PV-Anlagen **und
 * Speicher**: Ein Speicher ist eine Erzeugungsanlage hinter seinem Zähler, einen
 * eigenen Speicherzähler im Messkonzept gibt es seit #40 nicht mehr.
 */
export function assetKindsOf(slot: Slot): readonly ("pv" | "battery")[] {
  return isGenerationSlot(slot) ? ["pv", "battery"] : [];
}

/** Ob unter einem Steckplatz der Kasten „Erzeugungsanlagen" hängt. */
export function isAssetSlot(slot: Slot): boolean {
  return assetKindsOf(slot).length > 0;
}

const inputCount = (template: Template, slot: Slot): number =>
  template.inputs.filter((input) => input.slot === slot.key).length;

function single(slot: Slot, family: RoleFamily = "single"): ConceptRole {
  return {
    key: slot.key,
    label: family === "own_unit" ? "Selbst genutzte Einheit" : slot.label,
    hint: slot.hint,
    delivers: slot.delivers,
    family,
    slots: [slot],
    required: slot.required,
    many: false,
    add: null,
    // Erzeugung fließt in die Verteilung hinein, ein Unterzähler aus ihr heraus.
    up: /erzeug|einspeis/i.test(slot.label),
  };
}

export function conceptStructure(template: Template): ConceptStructure {
  const spec = template.layout;
  const behindHouse = consumersBehindHouse(template);
  const slots = template.slots;

  const connectionSlot = slots.find((slot) => slot.placement === "connection");
  const separateSlot = slots.find((slot) => slot.placement === "separate");
  const ownUnitSlot = slots.find(
    (slot) =>
      slot.repeat === "per_unit" &&
      inputCount(template, slot) > 1 &&
      slots.some((other) => other.placement === "own_bus"),
  );

  const houseConsumers = behindHouse ? slots.filter((slot) => slot.repeat === "per_consumer") : [];
  const unitSlots = slots.filter((slot) => slot.repeat === "per_unit" && slot !== ownUnitSlot);
  const behindUnit = new Map<string, Slot[]>();
  for (const unit of unitSlots) behindUnit.set(unit.key, []);
  if (ownUnitSlot !== undefined) behindUnit.set(ownUnitSlot.key, []);
  const branchConsumers: Slot[] = [];
  for (const slot of slots.filter((entry) => entry.repeat === "per_consumer" && !behindHouse)) {
    if (slot.placement === "own_bus" && ownUnitSlot !== undefined) {
      behindUnit.get(ownUnitSlot.key)?.push(slot);
      continue;
    }
    const unit =
      slot.siteAreaId === null
        ? undefined
        : unitSlots.find((entry) => entry.siteAreaId === slot.siteAreaId);
    if (unit !== undefined) behindUnit.get(unit.key)?.push(slot);
    else branchConsumers.push(slot);
  }
  const generators = slots.filter((slot) => slot.repeat === "per_generator");
  const ownGenerators = generators.filter((slot) => slot.placement === "own_bus");
  const branchGenerators = generators.filter((slot) => slot.placement !== "own_bus");
  const plain = slots.filter(
    (slot) =>
      slot.placement === "branch" && slot.repeat === "one" && !houseConsumers.includes(slot),
  );

  const generatorsAddable = spec !== null && generators.length < spec.generators.max;
  const generatorRole = (members: readonly Slot[], key: string): ConceptRole | null =>
    members.length === 0
      ? null
      : {
          key,
          label:
            members.length === 1 ? (members[0]?.label ?? "Erzeugungszähler") : "Erzeugungszähler",
          hint: members[0]?.hint ?? "",
          delivers: ["Erzeugung"],
          family: "generators",
          slots: members,
          required: members.some((slot) => slot.required),
          many: spec !== null && spec.generators.max > 1,
          add: generatorsAddable ? "generators" : null,
          up: true,
        };

  const unitsRole: ConceptRole | null =
    spec?.perUnit === true
      ? {
          key: "role:units",
          label: "Wohnungszähler",
          hint: "je Wohneinheit einer — zählt, was sie aus dem Haus bezieht; die Wohneinheiten kommen aus der Anlage",
          delivers: ["Verbrauch Wohneinheit"],
          family: "units",
          slots: unitSlots,
          required: unitSlots.some((slot) => slot.required),
          many: true,
          add: "units",
          up: false,
        }
      : null;

  const consumersAddable = spec !== null && spec.consumers && !behindHouse;
  const consumersRole: ConceptRole | null =
    branchConsumers.length > 0 || consumersAddable
      ? {
          key: "role:consumers",
          label: "Verbraucherzähler",
          hint: "Unterzähler weiterer Verbraucher hinter dem Summenzähler — Allgemeinstrom, Wärmepumpe, Wallbox",
          delivers: ["Verbrauch"],
          family: "consumers",
          slots: branchConsumers,
          required: branchConsumers.some((slot) => slot.required),
          many: true,
          add: consumersAddable ? "consumers" : null,
          up: false,
        }
      : null;

  const connection = connectionSlot === undefined ? null : single(connectionSlot);
  const separate = separateSlot === undefined ? null : single(separateSlot);
  const ownUnit = ownUnitSlot === undefined ? null : single(ownUnitSlot, "own_unit");
  const ownGeneratorRole = generatorRole(ownGenerators, "role:own-generators");
  const branchGeneratorRole = generatorRole(branchGenerators, "role:generators");
  const ownChildren = ownGeneratorRole === null ? [] : [ownGeneratorRole];

  const branches = [
    ...(ownUnit === null ? [] : [ownUnit]),
    ...(branchGeneratorRole === null ? [] : [branchGeneratorRole]),
    ...plain.map((slot) => single(slot)),
    ...(unitsRole === null ? [] : [unitsRole]),
    ...(consumersRole === null ? [] : [consumersRole]),
  ];

  const roles = [
    ...(connection === null ? [] : [connection]),
    ...(separate === null ? [] : [separate]),
    ...(ownUnit === null ? [] : [ownUnit]),
    ...ownChildren,
    ...branches.filter((role) => role !== ownUnit),
  ];

  return {
    connection,
    separate,
    branches,
    ownChildren,
    behindUnit,
    houseConsumers,
    houseRest: hasUnmeteredRest(template),
    roles,
  };
}
